import  { useEffect, useState } from "react";
import LOGO from "../../assets/img/logo.png";
import { useNavigate } from "react-router";
import KYCLevelOneDetails from "./levelOne/KYCLevelOneDetails";
import KYCLevelTwoDetails from "./levelTwo/KYCLevelTwoDetails";
import KYCLevelThreeDetails from "./levelThree/KYCLevelThreeDetails";
import KYCLevelFourDetails from "./levelFour/KYCLevelFourDetails";
import { useDispatch } from "react-redux";
import { CurrentUser } from "../../utils/redux/Auth/AuthSlice";
import { logoutAndNavigate } from "../../services/logoutService";

const KYCVerification = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [levels, setLevels] = useState([]);
  const [reload, setReload] = useState(false);
  const [status, setStatus] = useState("");
  const data = JSON.parse(localStorage.getItem("user_profile"));

  const current = data?.onboardingStatus;

  // Detail view states
  const [showLevelOneDetails, setShowLevelOneDetails] = useState(false);
  const [showLevelTwoDetails, setShowLevelTwoDetails] = useState(false);
  const [showLevelThreeDetails, setShowLevelThreeDetails] = useState(false);
  const [showLevelFourDetails, setShowLevelFourDetails] = useState(false);

  const calculateLevels = (userProfile) => {
    if (!userProfile) return [];

    const currentLevel = userProfile.onboardingStatus;
    // console.log("Calculating levels with status:", currentLevel);

    // Handle COMPLETED status first
    if (currentLevel === "COMPLETED") {
      return [
        { level: "Level One", status: "completed" },
        { level: "Level Two", status: "completed" },
        { level: "Level Three", status: "completed" },
        { level: "Level Four", status: "completed" },
      ];
    }

    return [
      {
        level: "Level One",
        status: currentLevel === "Level 1" ? "active" : "completed",
      },
      {
        level: "Level Two",
        status:
          currentLevel === "Level 2"
            ? "active"
            : currentLevel === "Level 1"
            ? "inactive"
            : "completed",
      },
      {
        level: "Level Three",
        status:
          currentLevel === "Level 3"
            ? "active"
            : currentLevel === "Level 2" || currentLevel === "Level 1"
            ? "inactive"
            : "completed",
      },
      {
        level: "Level Four",
        status:
          currentLevel === "Level 4"
            ? "active"
            : currentLevel !== "Level 4"
            ? "inactive"
            : "completed",
      },
    ];
  };

  const updateLevelsFromProfile = () => {
    try {
      const userProfile = JSON.parse(localStorage.getItem("user_profile"));

      if (userProfile?.onboardingStatus) {
        localStorage.setItem(
          "onboardingStatus",
          JSON.stringify(userProfile?.onboardingStatus)
        );
        setLevels(calculateLevels(userProfile));
        
        // Check if any non-completed pending item has status "Pending"
        const pendingItems = userProfile?.onboardingProgress?.pendingItems || [];
        const activePendingItems = pendingItems.filter(
          (item) => item.status !== "Completed"
        );
        const hasPendingStatus = activePendingItems.some(
          (item) => item.status === "Pending"
        );
        setStatus(hasPendingStatus ? "Pending" : activePendingItems[0]?.status || "");
      }
    } catch (error) {
      console.error("Error updating levels:", error);
    }
  };

  const fetchUserData = async () => {
    try {
      setIsLoading(true);
      await dispatch(CurrentUser());
      updateLevelsFromProfile();
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching user data:", error);
      setError("Error loading user data. Please try again.");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Handle COMPLETED status first
    if (current === "COMPLETED") {
      navigate("/dashboard");
    }
  }, [current]);

  useEffect(() => {
    fetchUserData();
  }, [dispatch, reload]);

  // View Details Functions
  const viewLevelOneDetails = () => setShowLevelOneDetails(true);
  const viewLevelTwoDetails = () => setShowLevelTwoDetails(true);
  const viewLevelThreeDetails = () => setShowLevelThreeDetails(true);
  const viewLevelFourDetails = () => setShowLevelFourDetails(true);

  const goBack = async () => {
    // Reset all detail views
    setShowLevelOneDetails(false);
    setShowLevelTwoDetails(false);
    setShowLevelThreeDetails(false);
    setShowLevelFourDetails(false);

    // Fetch fresh data and update the view
    await fetchUserData();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-color"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-color text-white rounded hover:bg-opacity-90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const user_profile = JSON.parse(localStorage.getItem("user_profile"));

  return (
    <div className="min-h-screen bg-white relative">
      <div className="container sm:px-6">
        <div className="flex flex-col lg:flex-row mx-auto justify-between">
          <div className="pt-6 lg:pt-10 lg:pl-6 ">
            <img src={LOGO} alt="Flex monie" className="h-8 sm:h-8 lg:h-10" />
          </div>

          <div className="max-w-2xl w-full py-6 lg:py-10 mx-auto">
            {showLevelOneDetails ? (
              <KYCLevelOneDetails
                onBack={goBack}
                data={user_profile}
                reload={reload}
                setReload={setReload}
                setShowLevelOneDetails={setShowLevelOneDetails}
                setShowLevelTwoDetails={setShowLevelTwoDetails}
              />
            ) : showLevelTwoDetails ? (
              <KYCLevelTwoDetails
                onBack={goBack}
                data={user_profile}
                reload={reload}
                setReload={setReload}
                setShowLevelTwoDetails={setShowLevelTwoDetails}
                setShowLevelThreeDetails={setShowLevelThreeDetails}
              />
            ) : showLevelThreeDetails ? (
              <KYCLevelThreeDetails
                onBack={goBack}
                data={user_profile}
                reload={reload}
                setReload={setReload}
                setShowLevelThreeDetails={setShowLevelThreeDetails}
                setShowLevelFourDetails={setShowLevelFourDetails}
              />
            ) : showLevelFourDetails ? (
              <KYCLevelFourDetails
                onBack={goBack}
                data={user_profile}
                reload={reload}
                setReload={setReload}
                setShowLevelFourDetails={setShowLevelFourDetails}
              />
            ) : (
              <div className="w-full md:ml-[-40px]">
                <div className="text-left mb-6">
                  <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 mb-2">
                    KYC Verification
                  </h2>
                  <p className="text-gray-500 text-sm sm:text-base">
                    You're almost there! Complete each level to gain access <br /> (Please refresh whenever your KYC Level is Pending)
                  </p>
                </div>

                <div className="space-y-4">
                  {/* Level One */}
                  <div
                    className={`flex flex-col items-center justify-between border rounded-lg transition-colors w-full
      ${
        levels[0].status === "active"
          ? "border-primary-color"
          : "border-gray-300"
      }`}
                  >
                    <div className="flex items-center space-x-3 w-full p-4">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full border-2 border-primary-color">
                        <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-primary-color"></div>
                      </div>
                      <span className="text-base sm:text-lg font-medium text-gray-700">
                        {levels[0].level}
                      </span>
                    </div>

                    <div className="w-full h-0.5 bg-gray-200"></div>

                    <div className="flex flex-row w-full">
                      <button
                        disabled={
                          levels[0].status !== "active"
                          // ||
                          // status === "InProgress"
                        }
                        onClick={viewLevelOneDetails}
                        className={`text-indigo-500 text-sm font-medium w-1/2 py-3 sm:py-4 hover:bg-gray-50
          ${
            levels[0].status === "completed" || levels[0].status === "inactive"
              ? "cursor-not-allowed"
              : "text-indigo-500"
          }`}
                      >
                        View Details
                      </button>
                      <button
                        onClick={viewLevelOneDetails}
                        disabled={
                          levels[0].status !== "active"
                          // ||
                          // status === "InProgress"
                        }
                        className={`py-3 sm:py-4 text-sm font-medium transition-colors w-1/2
          ${
            levels[0].status === "completed"
              ? "bg-[#17AB56] text-white cursor-not-allowed"
              : // : status === "InProgress"
              // ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              levels[0].status === "inactive"
              ? "bg-[#8388B6] text-white cursor-not-allowed"
              : "bg-primary-color text-white"
          }`}
                      >
                        {levels[0].status === "completed"
                          ? "Completed"
                          : levels[0].status === "inactive"
                          ? "Inactive"
                          : "Proceed"}
                      </button>
                    </div>
                  </div>

                  {/* Level Two */}
                  <div
                    className={`flex flex-col items-center justify-between border rounded-lg transition-colors w-full
      ${
        levels[1].status === "active"
          ? "border-primary-color"
          : "border-gray-300"
      }`}
                  >
                    <div className="flex items-center space-x-3 w-full p-4">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full border-2 border-primary-color">
                        <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-primary-color"></div>
                      </div>
                      <span className="text-base sm:text-lg font-medium text-gray-700">
                        {levels[1].level}
                      </span>
                    </div>

                    <div className="w-full h-0.5 bg-gray-200"></div>

                    <div className="flex flex-row w-full">
                      <button
                        disabled={
                          levels[1].status !== "active" || status === "Pending"
                        }
                        onClick={viewLevelTwoDetails}
                        className={`text-indigo-500 text-sm font-medium w-1/2 py-3 sm:py-4 hover:bg-gray-50
          ${
            levels[1].status === "completed" ||
            levels[1].status === "inactive" ||
            status === "Pending"
              ? "cursor-not-allowed"
              : "text-indigo-500"
          }`}
                      >
                        View Details
                      </button>
                      <button
                        onClick={viewLevelTwoDetails}
                        disabled={
                          levels[1].status !== "active" || status === "Pending"
                        }
                        className={`py-3 sm:py-4 text-sm font-medium transition-colors w-1/2
          ${
            levels[1].status === "completed"
              ? "bg-[#17AB56] text-white cursor-not-allowed"
              : status === "Pending"
              ? "bg-[#BF5F2733] text-[#A48B0E] cursor-not-allowed"
              : levels[1].status === "inactive"
              ? "bg-[#8388B6] text-white cursor-not-allowed"
              : "bg-primary-color text-white"
          }`}
                      >
                        {levels[1].status === "completed"
                          ? "Completed"
                          : status === "Pending"
                          ? "Pending"
                          : levels[1].status === "inactive"
                          ? "Inactive"
                          : "Proceed"}
                      </button>
                    </div>
                  </div>

                  {/* Level Three */}
                  <div
                    className={`flex flex-col items-center justify-between border rounded-lg transition-colors w-full
      ${
        levels[2].status === "active"
          ? "border-primary-color"
          : "border-gray-300"
      }`}
                  >
                    <div className="flex items-center space-x-3 w-full p-4">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full border-2 border-primary-color">
                        <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-primary-color"></div>
                      </div>
                      <span className="text-base sm:text-lg font-medium text-gray-700">
                        {levels[2].level}
                      </span>
                    </div>

                    <div className="w-full h-0.5 bg-gray-200"></div>

                    <div className="flex flex-row w-full">
                      <button
                        disabled={
                          levels[2].status !== "active" || status === "Pending"
                        }
                        onClick={viewLevelThreeDetails}
                        className={`text-indigo-500 text-sm font-medium w-1/2 py-3 sm:py-4 hover:bg-gray-50
          ${
            levels[2].status === "completed" ||
            levels[2].status === "inactive" ||
            status === "Pending"
              ? "cursor-not-allowed"
              : "text-indigo-500"
          }`}
                      >
                        View Details
                      </button>
                      <button
                        onClick={viewLevelThreeDetails}
                        disabled={
                          levels[2].status !== "active" || status === "Pending"
                        }
                        className={`py-3 sm:py-4 text-sm font-medium transition-colors w-1/2
          ${
            levels[2].status === "completed"
              ? "bg-[#17AB56] text-white cursor-not-allowed"
              : status === "Pending"
              ? "bg-[#BF5F2733] text-[#A48B0E] cursor-not-allowed"
              : levels[2].status === "inactive"
              ? "bg-[#8388B6] text-white cursor-not-allowed"
              : "bg-primary-color text-white"
          }`}
                      >
                        {levels[2].status === "completed"
                          ? "Completed"
                          : levels[2].status === "inactive"
                          ? "Inactive"
                          : status === "Pending"
                          ? "Pending"
                          : "Proceed"}
                      </button>
                    </div>
                  </div>

                  {/* Level Four */}
                  <div
                    className={`flex flex-col items-center justify-between border rounded-lg transition-colors w-full
      ${
        levels[3].status === "active"
          ? "border-primary-color"
          : "border-gray-300"
      }`}
                  >
                    <div className="flex items-center space-x-3 w-full p-4">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full border-2 border-primary-color">
                        <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-primary-color"></div>
                      </div>
                      <span className="text-base sm:text-lg font-medium text-gray-700">
                        {levels[3].level}
                      </span>
                    </div>

                    <div className="w-full h-0.5 bg-gray-200"></div>

                    <div className="flex flex-row w-full">
                      <button
                        disabled={levels[3].status !== "active"}
                        onClick={viewLevelFourDetails}
                        className={`text-indigo-500 text-sm font-medium w-1/2 py-3 sm:py-4 hover:bg-gray-50
          ${
            levels[3].status === "completed" || levels[3].status === "inactive"
              ? "cursor-not-allowed"
              : "text-indigo-500"
          }`}
                      >
                        View Details
                      </button>
                      <button
                        onClick={viewLevelFourDetails}
                        disabled={levels[3].status !== "active"}
                        className={`py-3 sm:py-4 text-sm font-medium transition-colors w-1/2
          ${
            levels[3].status === "completed"
              ? "bg-[#17AB56] text-white cursor-not-allowed"
              : levels[3].status === "inactive"
              ? "bg-[#8388B6] text-white cursor-not-allowed"
              : "bg-primary-color text-white"
          }`}
                      >
                        {levels[3].status === "completed"
                          ? "Completed"
                          : levels[3].status === "inactive"
                          ? "Inactive"
                          : "Proceed"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div
            onClick={() => {
              logoutAndNavigate(navigate);
            }}
            className="flex items-cente gap-2 cursor-pointer justify-end pt-6 lg:pt-10"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9"
                stroke="#25233A"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M16 17L21 12L16 7"
                stroke="#25233A"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M21 12H9"
                stroke="#25233A"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="text-[#25233A] text-[14px] font-[500]">
              Logout
            </span>
          </div>
        </div>

        <footer className="text-gray-400 text-xs text-center lg:text-right py-6 lg:absolute lg:bottom-8 lg:right-8 xl:right-16 w-full lg:w-auto mt-4">
          <p>© 2024 Powered by CICO serve</p>
        </footer>
      </div>
    </div>
  );
};

export default KYCVerification;
